import { authToken, UNAUTHORIZED_EVENT } from '@/core/api/auth-token'
import { env } from '@/core/config/env'

export class HttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly url: string,
    /** Pesan dari body respons BE (mis. { "error": "..." }), bila ada. */
    public readonly detail?: string,
  ) {
    super(detail || `HTTP ${status} ${statusText} (${url})`)
    this.name = 'HttpError'
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** Query params yang otomatis di-serialize. */
  params?: Record<string, string | number | boolean | undefined>
  body?: unknown
  /** Lampirkan access token tersimpan (default true). */
  auth?: boolean
  /** Authorization eksplisit (mis. `Bearer <login_token>` untuk select-tenant). */
  authorization?: string
}

function buildUrl(path: string, params?: RequestOptions['params']): string {
  // Gabungkan base + path secara manual. Jangan pakai `new URL(path, base)`
  // karena path berawalan '/' akan membuang prefix base (mis. /api/v1).
  const base = (env.apiBaseUrl || window.location.origin).replace(/\/+$/, '')
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const url = new URL(base + normalizedPath)
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) url.searchParams.set(key, String(value))
    }
  }
  return url.toString()
}

/** Ambil pesan error dari body (Go/Gin umumnya { error } atau { message }). */
async function extractErrorDetail(response: Response): Promise<string | undefined> {
  try {
    const data = await response.clone().json()
    if (data && typeof data === 'object') {
      return (data.error ?? data.message ?? data.msg) as string | undefined
    }
  } catch {
    /* body bukan JSON */
  }
  return undefined
}

/**
 * Wrapper fetch minimal & typed. Sengaja tanpa axios agar bundle kecil.
 * Menjadi satu-satunya titik keluar jaringan (auth header + handle 401).
 */
async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { params, body, headers, auth = true, authorization, ...rest } = options

  const usesStoredToken = auth && !authorization && Boolean(authToken.get())
  const authHeader =
    authorization ?? (auth && authToken.get() ? `Bearer ${authToken.get()}` : undefined)

  // Multipart: body diteruskan apa adanya dan Content-Type TIDAK di-set —
  // browser yang mengisinya lengkap dengan boundary.
  const isForm = body instanceof FormData

  const response = await fetch(buildUrl(path, params), {
    ...rest,
    headers: {
      ...(isForm ? {} : { 'Content-Type': 'application/json' }),
      ...(authHeader ? { Authorization: authHeader } : {}),
      ...headers,
    },
    body: isForm ? body : body !== undefined ? JSON.stringify(body) : undefined,
  })

  if (!response.ok) {
    // Token sesi ditolak → bersihkan & beri tahu app untuk redirect ke login.
    if (response.status === 401 && usesStoredToken) {
      authToken.clear()
      window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT))
    }
    throw new HttpError(
      response.status,
      response.statusText,
      response.url,
      await extractErrorDetail(response),
    )
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

/** Baca nama file dari Content-Disposition; BE-lah yang menamai berkas. */
function filenameFromDisposition(header: string | null): string | undefined {
  if (!header) return undefined
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (utf8) return decodeURIComponent(utf8[1])
  const plain = /filename="?([^";]+)"?/i.exec(header)
  return plain ? plain[1] : undefined
}

/** Unduh berkas biner (mis. .xlsx). Menempuh jalur auth & error yang sama. */
async function download(
  path: string,
  options: RequestOptions = {},
): Promise<{ blob: Blob; filename?: string }> {
  // Sengaja tidak menyebar `options` ke fetch: unduhan hanya perlu GET tanpa
  // body, dan menyebar apa adanya berisiko menyelipkan body ke request GET.
  const { params, auth = true, authorization, headers, signal } = options
  const usesStoredToken = auth && !authorization && Boolean(authToken.get())
  const authHeader =
    authorization ?? (auth && authToken.get() ? `Bearer ${authToken.get()}` : undefined)

  const response = await fetch(buildUrl(path, params), {
    method: 'GET',
    signal,
    headers: {
      ...(authHeader ? { Authorization: authHeader } : {}),
      ...headers,
    },
  })

  if (!response.ok) {
    if (response.status === 401 && usesStoredToken) {
      authToken.clear()
      window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT))
    }
    throw new HttpError(
      response.status,
      response.statusText,
      response.url,
      await extractErrorDetail(response),
    )
  }

  return {
    blob: await response.blob(),
    filename: filenameFromDisposition(response.headers.get('Content-Disposition')),
  }
}

export const httpClient = {
  download,
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}
